// Join status from groups.members_* — mirrors backend/server.py stakeholder logic.

export function normalizeRoleToken(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

export function normalizeNumberToken(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

export function parseNotJoinedDetail(detail) {
  const tokens = new Set();
  for (const rawEntry of String(detail ?? "").split(",")) {
    const entry = rawEntry.trim();
    if (!entry) continue;
    tokens.add(normalizeRoleToken(entry));
    if (entry.includes(":")) {
      const [role, number] = entry.split(":", 2);
      tokens.add(normalizeRoleToken(role));
      tokens.add(normalizeNumberToken(number));
    } else {
      tokens.add(normalizeNumberToken(entry));
    }
  }
  return tokens;
}

export function hasMembersJoinCheck(groupRow) {
  return Boolean(
    String(groupRow["Members Joined Count"] ?? "").trim() ||
    String(groupRow["Members Not Joined Count"] ?? "").trim() ||
    String(groupRow["Members Not Joined - Detail"] ?? "").trim()
  );
}

export function memberJoinStatus(role, name, number, notJoinedTokens, hasJoinCheck, provenJoined = false) {
  // Someone who has sent a message in the group is in the group — that is not
  // an inference, it is the message. It outranks members_*, which only exists
  // if a participant webhook happened to be processed: a group whose members
  // all joined before that path worked has no members_* at all, and everyone
  // in it was being shown as "Not Joined" while actively chatting.
  if (provenJoined) return "Joined";
  // A member with no number is a member nothing can see. The backend only
  // invites, and only lists as not-joined, a stakeholder it has a number for
  // (build_invited_stakeholders skips blank phones), so a numberless one never
  // appears in the detail string — and the rule below reads "absent from the
  // detail" as "joined". Hyundai Pune's CCM, GM, PSF and Receptionist all had
  // blank numbers and all showed Joined, in a group of three people.
  //
  // Said plainly rather than as "Not Joined", because the two need different
  // actions: "Not Joined" is someone who was invited and has not accepted, and
  // will flip to Joined by itself the moment they join. This one never will —
  // not when they are added during a complaint, not ever — because there is no
  // number to recognise them by. Somebody has to fill it in under Settings.
  if (!normalizeNumberToken(number)) return "No number";
  // No webhook members_* yet → treat as Not Joined (avoid "Unknown" in the UI).
  if (!hasJoinCheck) return "Not Joined";
  const tokens = [normalizeRoleToken(role), normalizeRoleToken(name), normalizeNumberToken(number)].filter(Boolean);
  const notJoined = tokens.some((t) => notJoinedTokens.has(t));
  return notJoined ? "Not Joined" : "Joined";
}

/**
 * `senderNumbers` is the set of numbers seen sending in this group's
 * message_log, normalised to their last 10 digits. Optional: callers without
 * the transcript to hand behave exactly as before.
 */
export function joinStatusFromGroup(groupRow, role, name, number, senderNumbers) {
  const hasCheck = hasMembersJoinCheck(groupRow);
  const notJoined = parseNotJoinedDetail(groupRow["Members Not Joined - Detail"]);
  const token = normalizeNumberToken(number);
  const proven = Boolean(token && senderNumbers && senderNumbers.has(token));
  return memberJoinStatus(role, name, number, notJoined, hasCheck, proven);
}

/** Numbers that have actually spoken in this group, for joinStatusFromGroup. */
export function sendersFromMessageLog(rows) {
  const out = new Set();
  for (const m of rows || []) {
    const token = normalizeNumberToken(m?.sender_number);
    if (token) out.add(token);
  }
  return out;
}

/** Numbers that joined the group and haven't left since, from group_events rows (oldest first). */
export function joinedNumbersFromEvents(rows) {
  const out = new Set();
  for (const ev of rows || []) {
    const token = normalizeNumberToken(ev?.event_detail?.wa_id);
    if (!token) continue;
    if (ev.event_type === "participant_added") out.add(token);
    else if (ev.event_type === "participant_removed") out.delete(token);
  }
  return out;
}

/**
 * Numbers added from the desk that have not joined since. Adding someone only
 * sends them an invite, so they count as joined once WhatsApp reports them
 * joining, or they speak in the group, after their latest add — not on the
 * strength of an earlier stay: someone removed and added back still has their
 * old join and old messages on file.
 */
export function awaitingJoinFromEvents(eventRows, messageRows) {
  const addedAt = new Map();
  for (const ev of eventRows || []) {
    if (ev?.event_type !== "member_added") continue;
    const token = normalizeNumberToken(ev?.event_detail?.number);
    if (token) addedAt.set(token, Date.parse(ev.occurred_at) || 0); // oldest first: the latest add wins
  }
  const joinedSinceAdd = (token, iso) => {
    if (addedAt.has(token) && (Date.parse(iso) || 0) >= addedAt.get(token)) addedAt.delete(token);
  };
  for (const ev of eventRows || []) {
    if (ev?.event_type === "participant_added") joinedSinceAdd(normalizeNumberToken(ev?.event_detail?.wa_id), ev.occurred_at);
  }
  for (const m of messageRows || []) joinedSinceAdd(normalizeNumberToken(m?.sender_number), m?.created_at);
  return new Set(addedAt.keys());
}